import * as React from 'react';
import { observer } from 'mobx-react';
import cn from 'classnames';

import {
  SelectorKey,
  SelectorOptions,
  SelectorSize,
  SelectorType
} from 'shared/newEntities/components/Selector';
import { IconComponent, IconSize } from 'shared/entities/components/Icon';
import { SearchIcon, ArrowIcon, CloseIcon } from 'shared/newComponents/icons';
import {
  renderTranslationString,
  TFunctionType,
  TranslationString,
  useTypedTranslation
} from 'shared/entities/localization';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType,
  TypographyWeight
} from 'shared/newEntities/components/Typography';
import CroppedContent from 'shared/newComponents/CroppedContent';
import { useUiStore } from 'stores/index';
import Reference from 'shared/newComponents/Reference';
import { TagProps, TagSize } from 'shared/newEntities/components/Tag';
import Tag from 'shared/newComponents/Tag';
import RightIconComponent from 'shared/newComponents/SelectorBase/RightIconComponent';
import { ComponentType } from 'shared/entities/components/Component';

import './Trigger.modules.scss';

type Props = {
  options: SelectorOptions;
  isSelectorOpened: boolean;
  searchText: string;
  onChangeSearchText: (value: string) => void;
  pluralize?: (count: number) => TranslationString;
  placeholder?: TranslationString | string;
  title?: TranslationString | string;
  Icon?: IconComponent;
  withoutSearch?: boolean;
  withCroppedTitle?: boolean;
  fontSize?: TypographySize;
  fontWeight?: TypographyWeight;
  fontColor?: TypographyColor;
  selectorSize?: SelectorSize;
  onRemove?: () => void;
} & (
  | {
      type: SelectorType.single;
      value: SelectorKey | null;
      onChange: (value: SelectorKey | null) => void;
      excludeFromTriggerCountIds?: never;
    }
  | {
      type: SelectorType.multi;
      value: SelectorKey[];
      onChange: (value: SelectorKey[]) => void;
      excludeFromTriggerCountIds?: SelectorKey[];
    }
);

const Trigger: React.FC<Props> = ({
  isSelectorOpened,
  searchText,
  onChangeSearchText,
  options,
  pluralize,
  placeholder,
  title,
  Icon,
  withoutSearch = false,
  withCroppedTitle = true,
  fontSize = TypographySize.m,
  fontWeight,
  fontColor,
  selectorSize,
  onRemove,
  ...rest
}: Props) => {
  const { t } = useTypedTranslation();
  const uiStore = useUiStore();

  const multiTriggerValue = React.useMemo((): SelectorKey[] | null => {
    if (rest.type !== SelectorType.multi) {
      return null;
    }

    return rest.value.filter(
      (id) => !(rest.excludeFromTriggerCountIds ?? []).includes(id)
    );
  }, [rest.value, rest.type, rest.excludeFromTriggerCountIds]);

  const IconComp = React.useMemo((): IconComponent | null => {
    const DefaultIcon = withoutSearch ? null : SearchIcon;
    if (title) {
      return null;
    }

    if (rest.type === SelectorType.single) {
      const entity = rest.value !== null ? options.entities[rest.value] : null;

      if (!isSelectorOpened) {
        if (entity) {
          return entity.Icon || Icon || null;
        }

        return Icon || DefaultIcon;
      }

      return DefaultIcon;
    }

    if (!isSelectorOpened) {
      if (!multiTriggerValue?.length) {
        return Icon || DefaultIcon;
      }

      return null;
    }

    return DefaultIcon;
  }, [
    rest.type,
    rest.value,
    isSelectorOpened,
    title,
    withoutSearch,
    Icon,
    multiTriggerValue
  ]);

  const titleObj = React.useMemo(():
    | {
        value: TranslationString | string;
        type: 'value';
        isDefault?: boolean;
        rightComponent?: React.ReactNode;
        extraComponent?: React.ReactNode;
        tag?: TagProps;
        hoverReference?: React.ReactNode;
      }
    | { type: 'search' } => {
    if (title) {
      return {
        value: title,
        type: 'value'
      };
    }

    const defaultTitleObj:
      | {
          value: TranslationString | string;
          type: 'value';
          isDefault?: boolean;
        }
      | { type: 'search' } = withoutSearch
      ? {
          type: 'value',
          value: placeholder || '',
          isDefault: true
        }
      : { type: 'search' };

    if (rest.type === SelectorType.single) {
      const entity = rest.value !== null ? options.entities[rest.value] : null;

      if (!entity) {
        return defaultTitleObj;
      }

      if (withoutSearch || !isSelectorOpened) {
        const {
          title,
          rightComponent,
          tagProps,
          extraComponent: entityExtra,
          hoverReference
        } = entity;

        let rightComponentNode: React.ReactNode = null;

        if (rightComponent) {
          const { reference, ...rightIconProps } = rightComponent;

          const rightIcon: React.ReactNode = (
            <RightIconComponent
              styleName="trigger__right-icon"
              {...rightIconProps}
            />
          );

          rightComponentNode = reference ? (
            <Reference
              disabledOnMobile
              title={reference}
              trigger={
                <div styleName="trigger__right-icon-container">{rightIcon}</div>
              }
            />
          ) : (
            rightIcon
          );
        }

        const extraComponentNode =
          entityExtra?.forValue ?? entityExtra?.forOption ?? null;

        return {
          value: title,
          type: 'value',
          rightComponent: rightComponentNode,
          extraComponent: extraComponentNode,
          tag: tagProps,
          hoverReference
        };
      }

      return { type: 'search' };
    }

    const valueLength =
      multiTriggerValue?.filter((id) => options.entities[id]).length ?? 0;

    if (!valueLength) {
      return defaultTitleObj;
    }

    if (withoutSearch || !isSelectorOpened) {
      return pluralize
        ? { value: pluralize(valueLength), type: 'value' }
        : {
            value: (t: TFunctionType) =>
              t('Selector.pluralize', {
                ns: 'newComponents',
                count: valueLength
              }),
            type: 'value'
          };
    }

    return { type: 'search' };
  }, [
    rest.type,
    options.entities,
    isSelectorOpened,
    rest.value,
    rest.excludeFromTriggerCountIds,
    pluralize,
    withoutSearch,
    multiTriggerValue
  ]);

  const handleChange = React.useCallback<
    React.ChangeEventHandler<HTMLInputElement>
  >(
    (e) => {
      onChangeSearchText(e.target.value);
    },
    [onChangeSearchText]
  );

  const handleRemove = React.useCallback(
    (e) => {
      e.stopPropagation();
      onRemove?.();
    },
    [onRemove]
  );

  const actualFontWeight =
    fontWeight ??
    ((titleObj.type === 'value' && titleObj.isDefault) ||
    fontSize === TypographySize.xs
      ? TypographyWeight.normal
      : TypographyWeight.heavy);

  const actualFontColor =
    fontColor ??
    (titleObj.type === 'value' && titleObj.isDefault
      ? TypographyColor.tertiary
      : TypographyColor.main);

  const hasValue =
    rest.type === SelectorType.single
      ? rest.value !== null
      : (multiTriggerValue?.length ?? 0) > 0;

  return (
    <div styleName="trigger">
      <div styleName="trigger__left">
        {IconComp && (
          <IconComp styleName="trigger__icon" iconSize={IconSize.XM} />
        )}
        {titleObj.type === 'value' &&
          (() => {
            const valueContent = (
              <div styleName="trigger__left">
                <div styleName="trigger__left-value">
                  <div styleName="trigger__left-container">
                    {withCroppedTitle ? (
                      <CroppedContent popupContent={titleObj.value}>
                        <Typography
                          fontType={TypographyType.text}
                          fontSize={fontSize}
                          fontWeight={actualFontWeight}
                          fontColor={actualFontColor}
                          styleName="trigger__value"
                        >
                          {titleObj.value}
                        </Typography>
                      </CroppedContent>
                    ) : (
                      <Typography
                        fontType={TypographyType.text}
                        fontSize={fontSize}
                        fontWeight={actualFontWeight}
                        fontColor={actualFontColor}
                        styleName="trigger__value"
                      >
                        {titleObj.value}
                      </Typography>
                    )}
                    {titleObj.tag && (
                      <Tag
                        styleName="trigger__left-tag"
                        size={TagSize.xs}
                        {...titleObj.tag}
                      />
                    )}
                    <div styleName="trigger__left-tag">
                      {titleObj.extraComponent}
                    </div>
                  </div>
                </div>
                {titleObj.rightComponent}
              </div>
            );

            return titleObj.hoverReference ? (
              <Reference
                disabledOnMobile
                title={titleObj.hoverReference}
                trigger={valueContent}
              />
            ) : (
              valueContent
            );
          })()}
        {titleObj.type === 'search' && (
          <input
            onClick={(e) => {
              if (isSelectorOpened) {
                e.stopPropagation();
              }
            }}
            autoFocus={uiStore.isMobile ? undefined : isSelectorOpened}
            tabIndex={uiStore.isMobile ? -1 : undefined}
            styleName={cn(
              'trigger__search',
              `trigger__search_font-size-${fontSize}`
            )}
            value={searchText}
            onChange={handleChange}
            placeholder={renderTranslationString(
              placeholder ||
                ((t) =>
                  t('Selector.search', {
                    ns: 'newComponents'
                  })),
              t
            )}
          />
        )}
      </div>
      {onRemove && hasValue && (
        <CloseIcon
          styleName="trigger__right-icon"
          iconSize={IconSize.S}
          action={{
            type: ComponentType.svg,
            onClick: handleRemove
          }}
        />
      )}
      <ArrowIcon
        styleName={cn(
          'trigger__right-icon',
          selectorSize === SelectorSize.small && 'trigger__right-icon_small'
        )}
        direction={isSelectorOpened ? 'top' : 'bottom'}
      />
    </div>
  );
};

export default observer(Trigger);
